import { useEffect, useState } from 'react';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { can } from '@/security/access.js';
import { fetchReportesKpis } from '@/services/reportes.js';

const GERENCIAL_ROLES = new Set(['admin', 'gerente', 'gerencial', 'director', 'directivo']);
const numberFormatter = new Intl.NumberFormat('es-AR');
const moneyFormatter = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const kpiDefinitions = [
  { key: 'guias_emitidas', label: 'Guías emitidas', format: (value) => numberFormatter.format(Number(value || 0)) },
  { key: 'guias_entregadas', label: 'Guías entregadas', format: (value) => numberFormatter.format(Number(value || 0)) },
  { key: 'guias_retenidas', label: 'Guías retenidas', format: (value) => numberFormatter.format(Number(value || 0)) },
  { key: 'total_facturado', label: 'Total facturado', format: (value) => moneyFormatter.format(Number(value || 0)) },
  { key: 'total_cobrado', label: 'Total cobrado', format: (value) => moneyFormatter.format(Number(value || 0)) },
  { key: 'pendiente_cobro', label: 'Pendiente de cobro', format: (value) => moneyFormatter.format(Number(value || 0)) },
];

export default function InformesDashboard() {
  const { user } = useAuth();
  const role = String(user?.rol || '').toLowerCase();
  const roles = user?.roles || [];
  const hasAccess = can(user, 'REPORTES_VER') || GERENCIAL_ROLES.has(role) || roles.some((item) => GERENCIAL_ROLES.has(String(item?.nombre || '').toLowerCase()));
  const [fechaDesde, setFechaDesde] = useState('');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!hasAccess) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    fetchReportesKpis(fechaDesde ? { fechaDesde } : {})
      .then((result) => { if (active) setPayload(result?.kpis || null); })
      .catch((err) => { if (active) setError(err?.message || 'No se pudieron cargar los informes.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fechaDesde, hasAccess]);

  const hasData = payload && Object.values(payload).some((value) => Number(value) !== 0);

  return (
    <main className="min-h-screen bg-[rgb(var(--bg))] px-4 py-6 text-[rgb(var(--fg))] md:px-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Informes</h1>
          <p className="mt-1 text-sm text-[rgb(var(--muted))]">Resumen general de actividad, facturación y cobranzas.</p>
        </header>

        {!hasAccess ? (
          <section role="alert" className="rounded-xl border border-[rgba(var(--danger),0.28)] p-4 text-sm">
            Acceso restringido. Este informe está disponible solo para roles gerenciales.
          </section>
        ) : (
          <>
            <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-4">
              <label htmlFor="informes-fecha-desde" className="text-sm font-medium">Fecha desde</label>
              <input id="informes-fecha-desde" type="date" value={fechaDesde} onChange={(event) => setFechaDesde(event.target.value)} className="mt-2 block rounded-lg border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2 text-sm" />
            </section>
            {loading && <p role="status" className="rounded-xl border border-[rgba(var(--border),0.6)] p-4 text-sm">Cargando informes…</p>}
            {error && <p role="alert" className="rounded-xl border border-[rgba(var(--danger),0.28)] p-4 text-sm">{error}</p>}
            {!loading && !error && payload && !hasData && <p className="rounded-xl border border-[rgba(var(--border),0.6)] p-4 text-sm">Sin datos para el período seleccionado.</p>}
            {!loading && !error && hasData && (
              <section aria-label="Indicadores generales" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {kpiDefinitions.map(({ key, label, format }) => (
                  <article key={key} className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-5">
                    <h2 className="text-sm text-[rgb(var(--muted))]">{label}</h2>
                    <p className="mt-2 text-2xl font-semibold">{format(payload[key])}</p>
                  </article>
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
